import type { GridValidRowModel } from '@mui/x-data-grid-premium';
import { MutableRefObject, ReactElement, ReactNode, useCallback, useEffect, useMemo } from 'react';
import styles from './combinedItemsDetalPanel.module.scss';
import { GridRowParams } from '@mui/x-data-grid';
import { CombinedItem } from '../../../../../core/models';
import {
    GridApi,
    GridCellParams,
    GridColDef,
    GridComparatorFn,
    GridGroupingColDefOverride,
    GridGroupingColDefOverrideParams,
    GridRowId,
    GridValueFormatterParams,
    GridValueGetterParams,
    MaterialDataGrid,
    GridBasicGroupNode,
} from '@enterprise/material-data-grid';
import classNames from 'classnames';
import { useStores } from '../../../../../hooks';
import { SiteEntity } from '../../../../../core/entity/SiteEntity';
import i18n from '../../../../../i18n';
import { SpotPill, SpotSvg } from '@enterprise/spot';
import { CombinedItemChild } from 'apps/client/src/core/models/datasource/combinedItems/parts';
import { CombinedItemState } from '../../../../../store';
import { head } from 'lodash';
import { observer } from 'mobx-react';
import { useCombinedItemDetailPanel } from '../hooks/useCombinedItemDetailPanel.hook';

export enum CombinedItemDetailsGridColumn {
    // todo: will be used if we decide to made mobile view for this grid
    Id = 'pimsId',
    Description = 'hospitalDescription',
    Order = 'order',
    Practice = 'provider.siteId',
    ItemType = 'itemType.id',
    SellingPrice = 'prices.listPrice',
    NonChargeable = 'nonChargeable',
    Quantity = 'quantity',
}

enum CellType {
    Link = 'link',
    Text = 'text',
    Pill = 'pill',
}

export enum ItemsType {
    Datapoint = 'datapoint',
    Animana = 'animana',
}

interface HeaderProps {
    label?: string | ReactElement;
}

interface CellProps {
    type?: CellType;
}

export const header = (props: HeaderProps) =>
    function Header(): ReactElement {
        const { label } = props;

        return (
            <div className={styles.headerContainer}>
                <div className={classNames('spot-typography__text--body', styles.headerCell)}>{label}</div>
            </div>
        );
    };

export const cell = (props: CellProps = {}) =>
    function Cell<V extends GridValidRowModel>(params: GridCellParams<V, CombinedItemChild>): ReactElement {
        const { type } = props;
        const { formattedValue, value } = params;

        const renderPill = () => {
            const isActive = (value as unknown as string) === 'Active';

            return (
                <SpotPill type={isActive ? 'positive' : 'secondary'} isOutline={true}>
                    {formattedValue as unknown as ReactNode}
                </SpotPill>
            );
        };

        return (
            <div className={styles.cellContainer}>
                <div
                    className={classNames(styles.cell, {
                        [styles.linkCell]: type === CellType.Link,
                        [styles.pillCell]: type === CellType.Pill,
                    })}
                >
                    {type === CellType.Pill && formattedValue ? renderPill() : <span>{formattedValue as unknown as ReactNode}</span>}
                </div>
            </div>
        );
    };

export const getGroupItems = (apiRef: MutableRefObject<GridApi>, rowId: GridRowId): CombinedItemChild[] => {
    const { dataRowIdToModelLookup, tree } = apiRef.current?.state.rows;
    const groupRowIds: GridRowId[] | undefined = (tree?.[rowId] as GridBasicGroupNode)?.children;

    return groupRowIds.map((id) => dataRowIdToModelLookup[id]) as CombinedItemChild[];
};

export interface CombinedItemDetailPanelParams extends GridRowParams<CombinedItem> {
    state?: CombinedItemState;
    onStateChange?: (state: CombinedItemState) => void;
    selection?: string[];
    onSelectionChange?: (state: (string | number)[]) => void;
    className?: string;
}

export const CombinedItemDetailPanel = observer(function CombinedItemDetailPanel(props: CombinedItemDetailPanelParams) {
    const {
        domain: { constantsStore, combinedItemStore },
    } = useStores();

    const { isLoadingDetailedItems, detailedItems, fetchDetailedItems, getRowId } = combinedItemStore;
    const { id, row, state, onStateChange, selection, onSelectionChange, className } = props;
    const {
        apiRef,
        columnSizes,
        pinnedColumns,
        onPinnedColumnsChange,
        onCellClick,
        onColumnWidthChange,
        columnVisibilityModel,
        onColumnVisibilityModelChange,
        sortModel,
        onSortModelChange,
        rowGroupingModel,
        onRowGroupingModelChange,
        onColumnOrderChange,
    } = useCombinedItemDetailPanel({ state, onStateChange });

    const sitesMap = useMemo(
        () => new Map<string, SiteEntity>(constantsStore.applications.map((item) => [item.provider.siteId, item])),
        [constantsStore.applications],
    );

    const currencyComparator: GridComparatorFn = (v1: string, v2: string, cellParams1) => {
        const sort = apiRef.current?.getSortModel().find((item) => item.field === cellParams1.field)?.sort;
        const [v1Min, v1Max] = v1.split('-');
        const [v2Min, v2Max] = v2.split('-');
        return Number((sort === 'desc' && v1Max) || v1Min) - Number((sort === 'desc' && v2Max) || v2Min);
    };

    const isAutoGenerated = (rowId: GridRowId): boolean | undefined => {
        return (apiRef.current?.getRowNode(rowId) as { isAutoGenerated?: boolean })?.isAutoGenerated;
    };

    const columnNames = useMemo(
        () =>
            new Map<CombinedItemDetailsGridColumn, string>([
                [CombinedItemDetailsGridColumn.Id, i18n.t('controlCenter:itemId', 'Id')],
                [CombinedItemDetailsGridColumn.Description, i18n.t('controlCenter:productDescription', 'Product Description')],
                [CombinedItemDetailsGridColumn.Order, i18n.t('controlCenter:order', 'Sequence')],
                [CombinedItemDetailsGridColumn.Practice, i18n.t('controlCenter:practice', 'Practice')],
                [CombinedItemDetailsGridColumn.ItemType, i18n.t('controlCenter:itemType', 'Item Type')],
                [CombinedItemDetailsGridColumn.SellingPrice, i18n.t('controlCenter:sellingPrice', 'Selling Price')],
                [CombinedItemDetailsGridColumn.NonChargeable, i18n.t('controlCenter:nonChargeable', 'Non Chargeable Product')],
                [CombinedItemDetailsGridColumn.Quantity, i18n.t('controlCenter:quantity', 'Quantity')],
            ]),
        [],
    );

    const columns = useMemo<GridColDef<CombinedItemChild>[]>(() => {
        const columns: GridColDef<CombinedItemChild>[] = [
            {
                field: CombinedItemDetailsGridColumn.Description,
                headerName: columnNames.get(CombinedItemDetailsGridColumn.Description),
                width: columnSizes.get(CombinedItemDetailsGridColumn.Description),
                disableColumnMenu: false,
                renderHeader: header({ label: columnNames.get(CombinedItemDetailsGridColumn.Description) }),
                renderCell: cell(),
                valueGetter: ({ row, rowNode }: GridValueGetterParams<CombinedItemChild, string>) => {
                    if (!(rowNode as { isAutoGenerated?: boolean }).isAutoGenerated) {
                        return row.hospitalDescription;
                    }

                    const items = getGroupItems(apiRef, rowNode.id);
                    if (!items?.length) {
                        return '-';
                    }

                    const first = items[0].hospitalDescription;
                    const containsOne = items.every((item) => item.hospitalDescription === first);

                    if (containsOne) {
                        return first;
                    } else {
                        return '--';
                    }
                },
            },
            {
                field: CombinedItemDetailsGridColumn.Order,
                headerName: columnNames.get(CombinedItemDetailsGridColumn.Order),
                width: columnSizes.get(CombinedItemDetailsGridColumn.Order),
                disableColumnMenu: false,
                renderHeader: header({ label: columnNames.get(CombinedItemDetailsGridColumn.Order) }),
                renderCell: cell(),
                valueGetter: ({ row, rowNode }: GridValueGetterParams<CombinedItemChild, string>) => {
                    if (!(rowNode as { isAutoGenerated?: boolean }).isAutoGenerated) {
                        return row.order;
                    }

                    const items = getGroupItems(apiRef, rowNode.id);
                    if (!items?.length) {
                        return '-';
                    }

                    const first = items[0].order;
                    const containsOne = items.every((item) => item.order === first);

                    if (containsOne) {
                        return first;
                    } else {
                        return '--';
                    }
                },
            },
            {
                field: CombinedItemDetailsGridColumn.Quantity,
                headerName: columnNames.get(CombinedItemDetailsGridColumn.Quantity),
                width: columnSizes.get(CombinedItemDetailsGridColumn.Quantity),
                disableColumnMenu: false,
                groupable: false,
                renderHeader: header({ label: columnNames.get(CombinedItemDetailsGridColumn.Quantity) }),
                renderCell: cell(),
                valueGetter: ({ row, rowNode }: GridValueGetterParams<CombinedItemChild, string>) => {
                    if (!(rowNode as { isAutoGenerated?: boolean }).isAutoGenerated) {
                        return row.quantity;
                    }

                    const items = getGroupItems(apiRef, rowNode.id);
                    if (!items?.length) {
                        return '-';
                    }

                    const first = items[0].quantity;
                    const containsOne = items.every((item) => item.quantity === first);

                    if (containsOne) {
                        return first;
                    }
                    return '--';
                },
            },
            {
                field: CombinedItemDetailsGridColumn.SellingPrice,
                headerName: columnNames.get(CombinedItemDetailsGridColumn.SellingPrice),
                sortComparator: currencyComparator,
                minWidth: columnSizes.get(CombinedItemDetailsGridColumn.SellingPrice),
                disableColumnMenu: false,
                groupable: false,
                renderHeader: header({ label: columnNames.get(CombinedItemDetailsGridColumn.SellingPrice) }),
                renderCell: cell(),
                valueGetter: ({ id, row, rowNode }: GridValueGetterParams<CombinedItemChild, number>) => {
                    if (!(rowNode as { isAutoGenerated?: boolean }).isAutoGenerated) {
                        const price = head(row?.priceBreaks)?.invoicePrice || 0;
                        return !isAutoGenerated(id) && (price >= 0 ? price.toFixed(2) : `(${(price * -1).toFixed(2)})`);
                    }

                    const items = getGroupItems(apiRef, rowNode.id);

                    if (!items?.length) {
                        return '-';
                    }

                    const maxItem = items.reduce(function (prev, curr) {
                        return (head(prev?.priceBreaks)?.invoicePrice || 0) > (head(curr?.priceBreaks)?.invoicePrice || 0) ? prev : curr;
                    });

                    const minItem = items.reduce(function (prev, curr) {
                        return (head(prev?.priceBreaks)?.invoicePrice || 0) < (head(curr?.priceBreaks)?.invoicePrice || 0) ? prev : curr;
                    });

                    let max = 0;
                    let min = 0;
                    if (head(maxItem?.priceBreaks)?.invoicePrice) {
                        max = head(maxItem?.priceBreaks)?.invoicePrice || 0;
                    }
                    if (head(minItem?.priceBreaks)?.invoicePrice) {
                        min = head(minItem?.priceBreaks)?.invoicePrice || 0;
                    }
                    const maxOutput = max >= 0 ? max?.toFixed(2) : `(${(max && max * -1)?.toFixed(2)})`;
                    const minOutput = min >= 0 ? min?.toFixed(2) : `(${(min && min * -1)?.toFixed(2)})`;

                    if (max === min) {
                        return maxOutput;
                    }

                    return `${minOutput} - ${maxOutput}`;
                },
            },
            {
                field: CombinedItemDetailsGridColumn.NonChargeable,
                headerName: columnNames.get(CombinedItemDetailsGridColumn.NonChargeable),
                width: columnSizes.get(CombinedItemDetailsGridColumn.NonChargeable),
                disableColumnMenu: false,
                groupable: false,
                renderHeader: header({ label: columnNames.get(CombinedItemDetailsGridColumn.NonChargeable) }),
                renderCell: cell(),
                valueGetter: ({ row, rowNode }: GridValueGetterParams<CombinedItemChild, string>) => {
                    const getValue = (value?: boolean) => (value ? 'yes' : 'no');

                    if (!(rowNode as { isAutoGenerated?: boolean }).isAutoGenerated) {
                        return getValue(row.nonChargeable);
                    }

                    const items = getGroupItems(apiRef, rowNode.id);
                    if (!items?.length) {
                        return '-';
                    }

                    const first = items[0].nonChargeable;
                    const containsOne = items.every((item) => item.nonChargeable === first);

                    if (containsOne) {
                        return getValue(first);
                    }
                    return '--';
                },
            },

            {
                field: CombinedItemDetailsGridColumn.Practice,
                headerName: columnNames.get(CombinedItemDetailsGridColumn.Practice),
                width: columnSizes.get(CombinedItemDetailsGridColumn.Practice),
                flex: 1,
                disableColumnMenu: false,
                groupable: false,
                renderHeader: header({ label: columnNames.get(CombinedItemDetailsGridColumn.Practice) }),
                renderCell: cell(),
                valueGetter: ({ row, rowNode }: GridValueGetterParams<CombinedItemChild, string>) => {
                    const getValue = (item: CombinedItemChild) => sitesMap.get(item?.provider.siteId)?.name;

                    if (!(rowNode as { isAutoGenerated?: boolean }).isAutoGenerated) {
                        return getValue(row);
                    }

                    const items = getGroupItems(apiRef, rowNode.id);
                    if (!items?.length) {
                        return '-';
                    }

                    const first = getValue(items[0]);
                    const containsOne = items.every((item) => getValue(item) === first);

                    if (containsOne) {
                        return first;
                    } else {
                        return '--';
                    }
                },
            },
        ];

        return columns;
    }, [apiRef, apiRef.current]);

    const groupColumn = useCallback(
        (params: GridGroupingColDefOverrideParams): GridGroupingColDefOverride<CombinedItemChild> => {
            const { fields } = params;
            const width = fields.includes(CombinedItemDetailsGridColumn.Description) ? 442 : 150;
            const labels = fields.reduce((agg, field) => {
                const name = columnNames.get(field as CombinedItemDetailsGridColumn);
                return (name && [...agg, name]) || agg;
            }, [] as string[]);
            const headerName = labels.join('/');

            return {
                width,
                headerName,
                renderHeader: header({
                    label: (
                        <>
                            {headerName}
                            <SpotSvg className={styles.titleIcon} icon="list" width="14" />
                        </>
                    ),
                }),
                filterable: true,
                valueFormatter: (params: GridValueFormatterParams) => {
                    const { value } = params;

                    return <span className={styles.group}>{value}</span>;
                },
            };
        },
        [apiRef, apiRef.current],
    );

    const getChildRowId = (row: CombinedItemChild) => row.pimsGeneratedId;

    const headerHeight = 48;
    const rowHeight = 48;

    useEffect(() => {
        (async () => {
            await fetchDetailedItems(row);
        })();
    }, [id, row]);

    const { isLoading, items } = useMemo(() => {
        return {
            isLoading: isLoadingDetailedItems && !detailedItems?.get(getRowId(row)),
            items: detailedItems?.get(getRowId(row))?.items || [],
        };
    }, [detailedItems?.get(getRowId(row)), isLoadingDetailedItems]);

    return (
        <>
            <MaterialDataGrid
                loading={isLoading}
                autoHeight={true}
                keepNonExistentRowsSelected
                className={classNames(styles.holder, className)}
                data-automation-id="combined-items-detail-grid"
                rows={items}
                columns={columns}
                apiRef={apiRef}
                rowHeight={rowHeight}
                headerHeight={headerHeight}
                rowGroupingColumnMode="multiple"
                getRowId={getChildRowId}
                rowSelectionModel={selection}
                onRowSelectionModelChange={onSelectionChange}
                groupingColDef={groupColumn}
                onCellClick={onCellClick}
                disableRowSelectionOnClick={true}
                disableColumnFilter={false}
                disableColumnResize={false}
                pinnedColumns={pinnedColumns}
                onPinnedColumnsChange={onPinnedColumnsChange}
                onColumnWidthChange={onColumnWidthChange}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={onColumnVisibilityModelChange}
                sortModel={sortModel}
                onSortModelChange={onSortModelChange}
                rowGroupingModel={rowGroupingModel}
                onRowGroupingModelChange={(model) => onRowGroupingModelChange(model)}
                onColumnOrderChange={onColumnOrderChange}
                classes={{
                    groupingCriteriaCell: styles.groupingCriteriaCell,
                    columnHeaders: styles.columnHeaders,
                    columnHeader: styles.columnHeader,
                    pinnedColumnHeaders: styles.pinnedColumnHeaders,
                    pinnedColumns: styles.pinnedColumns,
                }}
            />
        </>
    );
});
